import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Stack, Button, Spinner, Container } from "react-bootstrap";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchGuardian,
  patchMember,
  postBedOccupant,
  postGuardian,
  postJobAssignment,
  postMember,
  postNote,
} from "../../services/httpClient";
import DatePicker from "../ui/DatePicker";
import Input from "../ui/Input";
import SelectObject from "../ui/SelectObject";
import InputPhone from "../ui/InputPhone";
import { useParams } from "react-router-dom";
import ToastMsg from "../ui/ToastMsg";
import FormCheck from "../ui/FormCheckbox";
import HouseholdCard from "./HouseholdCard";
import { User } from "../../types/user";
import useApp from "../../utils/useApp";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import IntakeNote from "./IntakeNote";
import StandardNote from "./StandardNote";
import { UserFields } from "../../types/userFields";
import { validateUserFields } from "../../helpers/validation";

interface CreateGuardState {
  caseStatus: string;
  setShowCreateFamily: any;
  startingGuardians: any[];
  startingHousehold: any[];
  startingChildren: any[];
  setUserToSave: any;
  onClickDoneCreate: () => void;
  getHousehold: () => void;
}

const CreateMember = ({
  caseStatus,
  setShowCreateFamily,
  startingGuardians,
  startingHousehold,
  startingChildren,
  setUserToSave,
  onClickDoneCreate,
  getHousehold,
}: CreateGuardState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [guardians, setGuardians] = useState<User[]>(startingGuardians);
  const [household, setHousehold] = useState<User[]>(startingHousehold);
  const [children, setChildren] = useState<User[]>(startingChildren);
  const [type, setType] = useState<string>("");
  const [noteDate, setNoteDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [note, setNote] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [allRelationships, setAllRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [userFields, setUserFields] = useState<UserFields>({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    isPrimary: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });
  const mainRef = useRef();

  var types: any[] = [
    { name: "Primary Adult", key: "primary_adult" },
    { name: "Child", key: "child" },
    { name: "Other", key: "other" },
  ];

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }

    handleScroll(mainRef);
  }, []);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setAllRelationships(res[0].data);
      setRelationships(
        res[0].data.filter((c: any) => {
          return c.key !== "child";
        }),
      );
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms().then((res: any) => {
      const programs = res[0].data;
      const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
      setPrograms(updatedPrograms);
    });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);
      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        setFilteredBeds(bedList);
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    if (setUserToSave) setUserToSave(true);
    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      console.log(updated);

      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      return updated;
    });
  };

  const updateType = (e: any) => {
    setType(e.target.value);
    updateField("appAccess", false);
    if (e.target.value === "child") {
      updateField("relationship", "child");
    }
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.key);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.key);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("bed", e);
  };

  const onClickSave = (isDone: boolean) => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    setErrMsg("");

    if (validation.isValid) {
      if (type === "primary_adult") {
        var updatedUser: any = {
          appaccess: userFields.appAccess,
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          email: userFields.email,
          phonenumber: userFields.phone === "1" ? null : userFields.phone,
          relationship: userFields.relationship,
          birthdate: userFields.birthDate,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
          level_id: userFields.level?.id ? userFields.level.id : null,
          is_new: true,
        };

        setIsSaving(true);
        //create user
        postGuardian(updatedUser, id)
          .then((res: any) => {
            const userId = res[0].data.user_id;
            setSaveIsEnabled(false);
            setGuardians((prev) => [...prev, updatedUser]);
            clearUserState();
            setShowToast(true);
            setToastText("Primary Adult Created");
            const toast =
              userFields.appAccess && userFields.preferredName
                ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
                : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);

            if (caseStatus === "active") {
              //save intake note
              const newNote = {
                notetype: "intake",
                subject: {
                  id: userId,
                  type: "guardian",
                },
                fields: {
                  description: note,
                  date: new Date().toISOString().split("T")[0],
                },
              };

              postNote(newNote, id)
                .then((res: any) => {
                  const noteId = res[0].data.note_id;

                  //update user intake data
                  const intakeData = {
                    intake_at: noteDate,
                    intake_note_id: noteId,
                  };

                  patchGuardian(intakeData, id, userId).catch((err: any) => {
                    console.log(err);
                  });
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            } else if (note) {
              const newNote = {
                notetype: "standard",
                subject: {
                  id: userId,
                  type: "guardian",
                },
                fields: {
                  text: note,
                  date: noteDate,
                },
              };

              postNote(newNote, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            }

            if (userFields.job) {
              const newJob = {
                job_id: userFields.job?.id,
                user_id: userId,
              };

              postJobAssignment(newJob, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (auth?.profile.includeHMIS === 1) {
              if (userFields.bed) {
                const newBed = {
                  bed_id: userFields.bed?.id,
                  user_id: userId,
                };

                postBedOccupant(newBed, id)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }

            setUserToSave(false);
            if (isDone) {
              getHousehold();
              setShowCreateFamily(false);
            } else {
              getJobs();
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            setIsSaving(false);
          });
      } else if (type === "other" || type === "child") {
        const updatedMember: any = {
          role: "",
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          phonenumber: userFields.phone === "1" ? null : userFields.phone?.toString(),
          relationship: userFields.relationship,
          birthdate: userFields.birthDate,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          school: userFields.school,
          grade: userFields.grade,
          program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
          level_id: userFields.level?.id === 0 ? 0 : userFields.level?.id ? userFields.level.id : null,
          is_new: true,
        };

        setIsSaving(true);
        //create member
        postMember(updatedMember, id)
          .then((res: any) => {
            const memberId = res[0].data.member_id;
            setSaveIsEnabled(false);
            if (type === "other") {
              setHousehold((prev) => [...prev, updatedMember]);
            } else {
              setChildren((prev) => [...prev, updatedMember]);
            }
            clearUserState();
            setShowToast(true);
            setToastText("Member Created");
            const toast = userFields.preferredName
              ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
              : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);

            if (caseStatus === "active") {
              //save intake note
              const newNote = {
                notetype: "intake",
                subject: {
                  id: memberId,
                  type: "member",
                },
                fields: {
                  description: note,
                  date: new Date().toISOString().split("T")[0],
                },
              };

              postNote(newNote, id)
                .then((res: any) => {
                  const noteId = res[0].data.note_id;

                  //update member intake data
                  const intakeData = {
                    intake_at: noteDate,
                    intake_note_id: noteId,
                  };

                  patchMember(intakeData, id, memberId).catch((err: any) => {
                    console.log(err);
                  });
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            } else if (note) {
              const newNote = {
                notetype: "standard",
                subject: {
                  id: memberId,
                  type: "member",
                },
                fields: {
                  text: note,
                  date: noteDate,
                },
              };

              postNote(newNote, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            }

            if (userFields.job) {
              const newJob = {
                job_id: userFields.job?.id,
                member_id: memberId,
              };

              postJobAssignment(newJob, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (auth?.profile.includeHMIS === 1) {
              if (userFields.bed) {
                const newBed = {
                  bed_id: userFields.bed?.id,
                  member_id: memberId,
                };

                postBedOccupant(newBed, id)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }

            setUserToSave(false);
            if (isDone) {
              getHousehold();
              setShowCreateFamily(false);
            } else {
              getJobs();
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const clearUserState = () => {
    setType("");
    setUserFields({
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      phone: null,
      intakeDate: undefined,
      exitDate: undefined,
      birthDate: undefined,
      relationship: "",
      gender: "",
      ethnicity: "",
      race: "",
      school: "",
      grade: "",
      program: null,
      level: null,
      job: null,
      room: "",
      bed: null,
      isPrimary: false,
      appAccess: false,
      appAccessLast: undefined,
      isBanned: false,
    });
    setNote("");
    setNoteDate(new Date().toISOString().split("T")[0]);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isSaving ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4 className="app-header-text">{"Create Household Member"}</h4>
            </Col>
            <Col className="text-end">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-primary-color button" onClick={() => onClickDoneCreate()}>
                  Cancel
                </Button>
              </Stack>
            </Col>
          </Row>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={12} sm={12} md={12} lg={8} xl={6}>
              <Row className="mb-3">
                <Col xs={12}>
                  <SelectObject label="Household Member Type" value={type} options={types} onUpdate={(e: any) => updateType(e)} />
                </Col>
              </Row>

              {(type === "primary_adult" || type === "other" || type === "child") && (
                <>
                  <Stack gap={3} className="mb-3">
                    <Row>
                      <Col xs={6}>
                        <Input
                          label="First Name"
                          type="text"
                          value={userFields.firstName}
                          onUpdate={(e: any) => updateField("firstName", e.target.value)}
                          required={true}
                        />
                      </Col>
                      <Col xs={6}>
                        <Input
                          label="Last Name"
                          type="text"
                          value={userFields.lastName}
                          onUpdate={(e: any) => updateField("lastName", e.target.value)}
                          required={true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      {type === "primary_adult" && (
                        <Col xs={6}>
                          <Input
                            label="Email"
                            type="text"
                            value={userFields.email}
                            onUpdate={(e: any) => updateField("email", e.target.value)}
                            required={userFields.appAccess}
                          />
                        </Col>
                      )}
                      <Col xs={6}>
                        <InputPhone
                          label="Phone"
                          value={userFields.phone}
                          onUpdate={(e: any) => updateField("phone", e)}
                          required={type === "primary_adult" ? userFields.appAccess : false}
                        />
                      </Col>
                    </Row>
                    {type === "primary_adult" && (
                      <Row>
                        <Col xs={6} className="d-flex align-items-center">
                          <FormCheck
                            label="Mobile App Access"
                            css="mb-0"
                            value={userFields.appAccess}
                            onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                          />
                        </Col>
                        <small>{"(Requires email or phone)"}</small>
                      </Row>
                    )}
                    <Row>
                      <Col xs={6}>
                        <Input
                          label="Preferred Name"
                          type="text"
                          value={userFields.preferredName}
                          onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                        />
                      </Col>
                      <Col xs={6}>
                        <DatePicker
                          label="Birth Date"
                          currentValue={userFields.birthDate}
                          setCurrentValue={(e: any) => updateField("birthDate", e)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Relationship"
                          value={userFields.relationship}
                          options={type !== "primary_adult" ? allRelationships : relationships}
                          onUpdate={(e: any) => updateField("relationship", e.target.value)}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Gender"
                          value={userFields.gender}
                          options={genders}
                          onUpdate={(e: any) => updateField("gender", e.target.value)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Ethnicity"
                          value={userFields.ethnicity}
                          options={ethnicities}
                          onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Race"
                          value={userFields.race}
                          options={races}
                          onUpdate={(e: any) => updateField("race", e.target.value)}
                        />
                      </Col>
                    </Row>
                    {(programs.length > 0 || jobs.length > 0) && (
                      <Row>
                        {programs.length > 0 && (
                          <Col xs={6}>
                            <SelectObjectTypeahead
                              label="Program"
                              value={userFields.program}
                              options={programs}
                              onUpdate={(e: any) => updateProgram(e)}
                            />
                          </Col>
                        )}
                        {levels.length > 0 && (
                          <Col xs={6}>
                            <SelectObjectTypeahead
                              label="Program Level"
                              value={userFields.level}
                              options={levels}
                              onUpdate={(e: any) => updateField("level", e)}
                            />
                          </Col>
                        )}
                        {jobs.length > 0 && (
                          <Col xs={6} style={levels.length > 0 ? { marginTop: "1rem" } : {}}>
                            <SelectObjectTypeahead
                              label="Job"
                              value={userFields.job?.key}
                              options={jobs}
                              onUpdate={(e: any) => updateJob(e)}
                            />
                          </Col>
                        )}
                      </Row>
                    )}
                    {type === "child" && (
                      <Row>
                        <Col xs={6}>
                          <Input
                            label="School"
                            type="text"
                            value={userFields.school}
                            onUpdate={(e: any) => updateField("school", e.target.value)}
                          />
                        </Col>
                        <Col xs={4}>
                          <Input
                            label="Grade"
                            type="text"
                            value={userFields.grade}
                            onUpdate={(e: any) => updateField("grade", e.target.value)}
                          />
                        </Col>
                      </Row>
                    )}
                    {rooms.length > 0 && beds.length > 0 && (
                      <Row>
                        <Col xs={6}>
                          <SelectObjectTypeahead
                            label="Room"
                            value={userFields.room}
                            options={rooms}
                            onUpdate={(e: any) => updateRoom(e)}
                          />
                        </Col>
                        <Col xs={6}>
                          <SelectObjectTypeahead
                            label="Bed"
                            value={userFields.bed?.key}
                            options={filteredBeds}
                            onUpdate={(e: any) => updateBed(e)}
                            disabled={userFields.room ? false : true}
                            initialOption="Vacant"
                          />
                        </Col>
                      </Row>
                    )}
                  </Stack>
                  <h4 className="app-header-text mb-3">Note</h4>
                  <Row className="mb-3">
                    {caseStatus === "active" ? (
                      <IntakeNote intakeNote={note} setIntakeNote={setNote} intakeDate={noteDate} setIntakeDate={setNoteDate} />
                    ) : (
                      <StandardNote note={note} setNote={setNote} noteDate={noteDate} setNoteDate={setNoteDate} />
                    )}
                  </Row>
                </>
              )}
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={6}>
              <Row className="mb-2">
                {guardians.map((user: any, index: number) => (
                  <HouseholdCard key={index} user={user} showDetails={false} />
                ))}
                {household.map((user: any, index: number) => (
                  <HouseholdCard key={index} user={user} showDetails={false} />
                ))}
                {children.map((user: any, index: number) => (
                  <HouseholdCard key={index} user={user} showDetails={false} />
                ))}
              </Row>
            </Col>
          </Row>
          {(type === "primary_adult" || type === "other" || type === "child") && (
            <Row className="pt-0 pb-5">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-secondary-color button" onClick={() => onClickSave(false)} disabled={!saveIsEnabled}>
                  Save and Add Another
                </Button>
                <div className="vr" />
                <Button className="app-secondary-color button" onClick={() => onClickSave(true)} disabled={!saveIsEnabled}>
                  Save and Close
                </Button>
              </Stack>
            </Row>
          )}
        </>
      )}
      <Row ref={mainRef}></Row>
    </>
  );
};

export default CreateMember;
